<script setup>
// NOTICE: KAPTION-INTEGRATION
// agent-presence.VISIBILITY.3
//
// Says out loud what the availability selector above it cannot: the selector
// shows the *setting*, and while the heartbeat is not reaching the server that
// setting is not what anyone else sees. Without this the failure is invisible —
// the menu reads "En línea", the agent loses their dot in every teammate's
// roster, and auto-assignment skips them.
//
// It also states the limit of the HTTP presence refresh (agent-presence.REFRESH.1):
// picking a status marks the agent present again, but presence is a short server
// window fed by the heartbeat, so it lapses again until the heartbeat is back.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKaptionLivePresence } from 'dashboard/composables/useKaptionLivePresence';
import Button from 'next/button/Button.vue';

const { t } = useI18n();
const { lastPresenceRefreshAt } = useKaptionLivePresence();

const refreshedAtLabel = computed(() => {
  if (!lastPresenceRefreshAt.value) return null;

  return t('SIDEBAR.LIVE_PRESENCE.REFRESHED_AT', {
    time: lastPresenceRefreshAt.value.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    }),
  });
});

const reloadPage = () => {
  window.location.reload();
};
</script>

<template>
  <div
    data-testid="live-presence-notice"
    class="flex flex-col gap-2 px-3 py-2.5 mx-1 mb-1 border rounded-lg bg-n-amber-3 border-n-amber-4"
  >
    <div class="flex items-start gap-2">
      <span
        class="i-lucide-wifi-off size-4 shrink-0 mt-0.5 text-n-amber-11"
        aria-hidden="true"
      />
      <div class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('SIDEBAR.LIVE_PRESENCE.TITLE') }}
        </span>
        <span class="text-xs leading-relaxed text-n-slate-11">
          {{ $t('SIDEBAR.LIVE_PRESENCE.BODY') }}
        </span>
        <span
          v-if="refreshedAtLabel"
          data-testid="live-presence-refreshed-at"
          class="text-xs leading-relaxed text-n-slate-11"
        >
          {{ refreshedAtLabel }}
        </span>
      </div>
    </div>
    <Button
      size="sm"
      color="amber"
      variant="faded"
      icon="i-lucide-refresh-ccw"
      class="self-start"
      :label="$t('SIDEBAR.LIVE_PRESENCE.RELOAD')"
      @click="reloadPage"
    />
  </div>
</template>
